import { Box } from '@mui/material';
import {
  DataGrid,
  type DataGridProps,
  type GridPaginationModel,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';

import type { ListQuery, PageResult } from './types';

export interface ServerDataGridProps<
  Row extends GridValidRowModel,
  Sort extends string,
  Filters extends object,
> extends Omit<
  DataGridProps<Row>,
  | 'onPaginationModelChange'
  | 'onSortModelChange'
  | 'paginationMode'
  | 'paginationModel'
  | 'rowCount'
  | 'rows'
  | 'sortingMode'
  | 'sortModel'
> {
  readonly onQueryChange: (query: ListQuery<Sort, Filters>) => void;
  readonly page: PageResult<Row>;
  readonly query: ListQuery<Sort, Filters>;
}

export function ServerDataGrid<
  Row extends GridValidRowModel,
  Sort extends string,
  Filters extends object,
>({ onQueryChange, page, query, ...dataGridProps }: ServerDataGridProps<Row, Sort, Filters>) {
  const paginationModel: GridPaginationModel = {
    page: normalizePage(query.page),
    pageSize: normalizePageSize(query.pageSize),
  };
  const sortModel: GridSortModel = [{ field: query.sort, sort: query.order }];

  return (
    <Box sx={{ minHeight: 400, width: '100%' }}>
      <DataGrid
        {...dataGridProps}
        onPaginationModelChange={(next) => {
          const pageNumber = normalizePage(next.page);
          const pageSize = normalizePageSize(next.pageSize);
          if (pageNumber === query.page && pageSize === query.pageSize) return;
          onQueryChange({ ...query, page: pageNumber, pageSize });
        }}
        onSortModelChange={(next) => {
          const sort = firstValidSort(next);
          if (sort === null || (sort.field === query.sort && sort.order === query.order)) return;
          onQueryChange({ ...query, order: sort.order, page: 0, sort: sort.field as Sort });
        }}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        pageSizeOptions={dataGridProps.pageSizeOptions ?? defaultPageSizeOptions(query.pageSize)}
        rowCount={normalizeTotal(page.total)}
        rows={page.items}
        sortingMode="server"
        sortModel={sortModel}
      />
    </Box>
  );
}

function firstValidSort(
  model: GridSortModel,
): { readonly field: string; readonly order: 'asc' | 'desc' } | null {
  const sort = model[0];
  if (sort === undefined || sort.field.trim().length === 0) return null;
  if (sort.sort !== 'asc' && sort.sort !== 'desc') return null;
  return { field: sort.field, order: sort.sort };
}

function normalizePage(value: number): number {
  return Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

function normalizePageSize(value: number): number {
  return Number.isSafeInteger(value) && value > 0 ? value : 25;
}

function normalizeTotal(value: number): number {
  return Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

function defaultPageSizeOptions(pageSize: number): readonly number[] {
  return [...new Set([pageSize, 25, 50, 100])].sort((left, right) => left - right);
}
